/* ==========================================================================
   3M Textile — Animations (phase 3)

   Reproduction du site de référence https://wearix.framer.website/, d'après
   le relevé exhaustif docs/animations/releve-wearix.md : valeurs lues dans
   le code publié par Framer (JSON d'apparition et modules de chaque page),
   pas estimées à l'œil.

   Framer anime avec des ressorts ; on les reproduit en GSAP en résolvant
   chaque ressort exactement (système sur-amorti, vitesse initiale nulle) et
   en donnant cette courbe à GSAP comme fonction d'accélération. Même
   mouvement, autre moteur.

   Ce qui est animé :
     1. le défilement doux de toute la page (Lenis, 0,8 s) ;
     2. l'entrée de l'en-tête et des textes de la hero, au chargement ;
     3. la montée des blocs de contenu quand la moitié en est visible ;
     4. le visuel de la fiche produit, qui arrive de 0,85 ;
     5. le libellé des boutons, qui roule au survol.

   Rien n'est joué si le système demande de réduire les animations, ni dans
   l'éditeur de thème, où le contenu doit rester visible pour être réglé.
   ========================================================================== */
(function () {
  'use strict';

  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  if (!gsap || !ScrollTrigger) return;
  gsap.registerPlugin(ScrollTrigger);

  var REDUIT = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var EDITEUR = !!(window.Shopify && window.Shopify.designMode);
  var SURVOL = window.matchMedia('(hover: hover)').matches;

  /* --- 1. Les ressorts de Wearix ------------------------------------------ */

  // Vitesse (décision du client, 28/09/2026) : les durées de Wearix, même
  // ralenties, ne convenaient pas. On revient exactement à la vitesse de la
  // première ébauche, validée avant la phase 3 : la courbe relevée image par
  // image sur Wearix, jouée sur 1,1 s, 0,09 s entre deux voisins.
  var RELEVE = [
    [0, 0], [13, 0.0002], [33, 0.0452], [58, 0.1413], [83, 0.2378],
    [108, 0.3254], [133, 0.4033], [158, 0.4724], [183, 0.5334],
    [208, 0.5873], [233, 0.6351], [258, 0.6773], [283, 0.7146],
    [308, 0.7476], [333, 0.7768], [358, 0.8026], [383, 0.8255]
  ];
  var DUREE = 1.1;
  var DECALAGE = 0.09;
  function brut(ms) {
    if (ms <= 0) return 0;
    if (ms >= 383) return 1 - (1 - 0.8255) * Math.pow(0.884, (ms - 383) / 25);
    for (var i = 1; i < RELEVE.length; i++) {
      if (ms <= RELEVE[i][0]) {
        var a = RELEVE[i - 1], b = RELEVE[i];
        return a[1] + (b[1] - a[1]) * (ms - a[0]) / (b[0] - a[0]);
      }
    }
    return 1;
  }
  var FIN = brut(DUREE * 1000);
  var VITESSE = { duree: DUREE, ease: function (p) { return brut(p * DUREE * 1000) / FIN; } };

  // Réponse indicielle d'un ressort sur-amorti (masse 1, vitesse initiale
  // nulle), arrêtée quand il reste 0,1 % du trajet — ce que Framer ne
  // distingue plus du repos. Renvoie { ease, duree } pour GSAP.
  function ressort(raideur, amortissement) {
    var disc = Math.sqrt(amortissement * amortissement - 4 * raideur);
    var r1 = (-amortissement + disc) / 2;   // racine lente
    var r2 = (-amortissement - disc) / 2;   // racine rapide
    function x(t) {
      return 1 - (r2 * Math.exp(r1 * t) - r1 * Math.exp(r2 * t)) / (r2 - r1);
    }
    var duree = 0;
    while (1 - x(duree) > 0.001) duree += 0.001;
    var fin = x(duree);
    return {
      duree: duree,
      ease: function (p) { return p >= 1 ? 1 : x(p * duree) / fin; }
    };
  }

  // Toutes les animations à la même vitesse (voir plus haut). La fonction
  // ressort() reste disponible pour un réglage ultérieur.
  var R = {
    revele: VITESSE, entete: VITESSE, mots: VITESSE,
    lignes: VITESSE, boutons: VITESSE, produit: VITESSE
  };

  /* --- 2. Défilement doux ------------------------------------------------- */

  // Équivalent du composant « Smooth Scroll » de Wearix : Lenis, durée
  // 0,8 s, courbe exponentielle sortante. Seule la molette est lissée ; le
  // tactile, le clavier et la barre de défilement restent natifs. Tout
  // élément qui défile lui-même (tiroir, fenêtre, liste) garde sa molette.
  function defilementDoux() {
    var DUREE = 0.8;
    var courbe = function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); };
    var html = document.documentElement;
    var depart = 0, arrivee = 0, debut = 0, enCours = false;

    function bloque() {
      var b = document.body;
      return b.classList.contains('m3-locked') ||
        getComputedStyle(html).overflow === 'hidden' ||
        getComputedStyle(b).overflow === 'hidden';
    }
    function defileSeul(el) {
      for (; el && el !== document.body; el = el.parentElement) {
        var s = getComputedStyle(el);
        if ((s.overflowY === 'auto' || s.overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return true;
      }
      return false;
    }
    function max() { return html.scrollHeight - window.innerHeight; }

    function image(now) {
      var p = Math.min(1, (now - debut) / (DUREE * 1000));
      window.scrollTo(0, depart + (arrivee - depart) * courbe(p));
      if (p < 1) requestAnimationFrame(image); else enCours = false;
    }

    window.addEventListener('wheel', function (e) {
      if (e.ctrlKey || e.defaultPrevented || bloque() || defileSeul(e.target)) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      e.preventDefault();
      var pas = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? window.innerHeight : 1);
      var base = enCours ? arrivee : window.scrollY;
      arrivee = Math.max(0, Math.min(max(), base + pas));
      depart = window.scrollY;
      debut = performance.now();
      if (!enCours) { enCours = true; requestAnimationFrame(image); }
    }, { passive: false });

    // Le défilement doux du thème de base ferait lisser chaque image deux fois.
    html.style.scrollBehavior = 'auto';
  }

  /* --- 3. Découpage des textes -------------------------------------------- */

  // Enveloppe chaque mot dans un <span>, en gardant les <br> et le texte
  // lisible par les lecteurs d'écran (le découpage est purement visuel).
  function decouperMots(el) {
    if (el.dataset.m3Decoupe) return el.querySelectorAll('.m3-mot');
    el.dataset.m3Decoupe = '1';
    var morceaux = [];
    Array.prototype.slice.call(el.childNodes).forEach(function (n) {
      if (n.nodeType === 3) {
        n.textContent.split(/(\s+)/).forEach(function (bout) {
          if (!bout) return;
          if (/^\s+$/.test(bout)) { morceaux.push(document.createTextNode(bout)); return; }
          var s = document.createElement('span');
          s.className = 'm3-mot';
          s.textContent = bout;
          morceaux.push(s);
        });
      } else {
        morceaux.push(n);
      }
    });
    el.textContent = '';
    morceaux.forEach(function (m) { el.appendChild(m); });
    return el.querySelectorAll('.m3-mot');
  }

  // Regroupe les mots par ligne affichée : une ligne = même hauteur.
  function lignes(el) {
    var mots = decouperMots(el), groupes = [], y = null;
    Array.prototype.forEach.call(mots, function (m) {
      var top = m.offsetTop;
      if (y === null || Math.abs(top - y) > 2) { groupes.push([]); y = top; }
      groupes[groupes.length - 1].push(m);
    });
    return groupes;
  }

  /* --- 4. Entrées au chargement -------------------------------------------- */

  function entrees() {
    // En-tête : y −12 px, ressort 320/52, sans délai.
    var entete = document.querySelector('.m3-header');
    if (entete) {
      gsap.fromTo(entete, { opacity: 0, y: -12 }, {
        opacity: 1, y: 0, duration: R.entete.duree, ease: R.entete.ease, clearProps: 'opacity,transform'
      });
    }

    // Titres : chaque mot, y 10 px, départ 0,05 s puis 0,05 s d'écart.
    var titres = document.querySelectorAll('.m3-hero__title, .m3-pagehead h1, .m3-contact__intro h1');
    Array.prototype.forEach.call(titres, function (t) {
      var mots = decouperMots(t);
      gsap.fromTo(mots, { opacity: 0, y: 10 }, {
        opacity: 1, y: 0, duration: R.mots.duree, ease: R.mots.ease,
        delay: 0.1, stagger: DECALAGE, clearProps: 'opacity,transform'
      });
    });

    // Textes d'introduction : chaque ligne, y 10 px, départ 0,1 s.
    var textes = document.querySelectorAll('.m3-hero__subtitle, .m3-pagehead__sub, .m3-contact__intro > p.m3-muted');
    Array.prototype.forEach.call(textes, function (p) {
      // Un texte riche (paragraphes, liens) garde sa structure : il monte d'un bloc.
      var groupes = p.children.length && !/^(BR|SPAN)$/.test(p.children[0].tagName) ? [[p]] : lignes(p);
      groupes.forEach(function (ligne, i) {
        gsap.fromTo(ligne, { opacity: 0, y: 10 }, {
          opacity: 1, y: 0, duration: R.lignes.duree, ease: R.lignes.ease,
          delay: 0.1 + i * DECALAGE, clearProps: 'opacity,transform'
        });
      });
    });

    // Boutons et sélecteur de la hero : fondu seul, 0,15 s.
    var boutons = document.querySelectorAll('.m3-hero__actions, .m3-hero__rail');
    if (boutons.length) {
      gsap.fromTo(boutons, { opacity: 0 }, {
        opacity: 1, duration: R.boutons.duree, ease: R.boutons.ease, delay: 0.1 + 3 * DECALAGE, clearProps: 'opacity'
      });
    }

    // Fiche produit : le visuel arrive de 0,85 et de 50 px plus bas, 0,25 s.
    var visuel = document.querySelector('.m3-pdp__visual');
    if (visuel) {
      gsap.fromTo(visuel, { opacity: 0, scale: 0.85, y: 50 }, {
        opacity: 1, scale: 1, y: 0, duration: R.produit.duree, ease: R.produit.ease,
        delay: 0.1, clearProps: 'opacity,transform'
      });
    }
  }

  /* --- 5. Montée au défilement --------------------------------------------- */

  // Chaque entrée : les éléments qui montent ensemble, d'un seul
  // déclencheur. Wearix ne met pas de cascade entre voisins : chaque carte a
  // son propre déclencheur, les cartes d'une même rangée partent ensemble.
  // Les titres de section ne bougent pas, comme sur Wearix.
  var BLOCS = [
    // Grilles de produits : la grille entière, d'un bloc (Best sellers, Boutique).
    { un: '.m3-grid' },
    // Histoire de marque : texte et photos ensemble (bloc vidéo de Wearix).
    { ensemble: ['.m3-story__body', '.m3-story__photos'], dans: '.m3-story' },
    // Collections : chaque rangée (cartes collection de Wearix).
    { un: '.m3-rows > .m3-row' },
    // Nos principes : chaque tuile (Styles & Wear de Wearix).
    { un: '.m3-bento > .m3-bento__tile' },
    // Journal : chaque article (Blog de Wearix).
    { un: '.m3-posts > .m3-post' },
    // Contact et FAQ : chaque bloc (cartes de contact de Wearix).
    { un: '.m3-contact__visual' }, { un: '.m3-contact__panel' },
    { un: '.m3-faq__main' }, { un: '.m3-faq__showroom' }
  ];

  // Déclenchement : dès que le haut du bloc entre dans l'écran. Wearix
  // attend que la moitié du bloc soit visible ; sur nos grandes grilles
  // (catalogue, collections), la page restait vide sous le titre en
  // attendant. Choix du client le 28/09/2026 : monter dès que la section
  // est visible — au chargement si elle l'est déjà.
  function depart() { return 'top bottom'; }

  function monter(cibles, declencheur) {
    gsap.set(cibles, { opacity: 0, y: 50 });
    gsap.to(cibles, {
      opacity: 1, y: 0, duration: R.revele.duree, ease: R.revele.ease,
      clearProps: 'opacity,transform',
      scrollTrigger: { trigger: declencheur, start: depart(), once: true }
    });
  }

  function reveler(racine) {
    racine = racine || document;
    BLOCS.forEach(function (b) {
      if (b.un) {
        racine.querySelectorAll(b.un).forEach(function (el) {
          if (el.dataset.m3Anime || el.closest('.m3-hero, .m3-header, .m3-ov, .m3-menu, .m3-news-modal')) return;
          el.dataset.m3Anime = '1';
          monter(el, el);
        });
      } else {
        racine.querySelectorAll(b.dans).forEach(function (sec) {
          if (sec.dataset.m3Anime) return;
          var cibles = b.ensemble.map(function (s) { return sec.querySelector(s); }).filter(Boolean);
          if (!cibles.length) return;
          sec.dataset.m3Anime = '1';
          monter(cibles, sec);
        });
      }
    });
  }

  /* --- 6. Libellé des boutons qui roule au survol ------------------------- */

  // Le libellé sort par le haut et une copie identique entre par le bas
  // (bouton de Wearix). La copie est masquée aux lecteurs d'écran.
  function roulerBoutons(racine) {
    if (!SURVOL) return;
    (racine || document).querySelectorAll('.m3-btn').forEach(function (btn) {
      if (btn.dataset.m3Roule) return;
      // Le libellé : un <span> enfant sans icône, sinon le texte direct.
      var cible = null;
      Array.prototype.forEach.call(btn.children, function (c) {
        if (!cible && c.tagName === 'SPAN' && !c.querySelector('svg') && c.textContent.trim()) cible = c;
      });
      var texte;
      if (cible) {
        texte = cible.textContent.trim();
      } else {
        var noeuds = Array.prototype.filter.call(btn.childNodes, function (n) {
          return n.nodeType === 3 && n.textContent.trim();
        });
        if (noeuds.length !== 1) return;
        texte = noeuds[0].textContent.trim();
        cible = document.createElement('span');
        btn.replaceChild(cible, noeuds[0]);
      }
      btn.dataset.m3Roule = '1';
      cible.classList.add('m3-roll');
      cible.textContent = '';
      var a = document.createElement('span'); a.className = 'm3-roll__a'; a.textContent = texte;
      var b = document.createElement('span'); b.className = 'm3-roll__b'; b.textContent = texte;
      b.setAttribute('aria-hidden', 'true');
      cible.appendChild(a); cible.appendChild(b);
    });
  }

  /* --- Démarrage ----------------------------------------------------------- */

  function demarrer() {
    if (REDUIT || EDITEUR) return;
    // Défilement doux suspendu le 28/09/2026 : la version maison saccadait
    // au pavé tactile (signalé par le client). À reprendre avec Lenis, la
    // bibliothèque qu'utilise Wearix, plutôt qu'avec une imitation.
    // defilementDoux();
    entrees();
    reveler();
    roulerBoutons();
    // Les polices et les images changent les hauteurs : on recale les seuils.
    window.addEventListener('load', function () { ScrollTrigger.refresh(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }

  window.M3 = window.M3 || {};
  window.M3.animer = function (racine) {
    if (REDUIT || EDITEUR) return;
    reveler(racine);
    roulerBoutons(racine);
  };
  window.M3.ressort = ressort;
})();
